/**
 * 挑料。`by_id` 與 `by_weight` 的判定差異即時顯示。
 *
 * §7.5 的核心：**「需要『打字就要看到結果』的判定留在前端」**，
 * 用同一份 `shared/derive/` 程式碼。`judgePick()` 就是規格書點名的那一個：
 * 「挑片時切換 by_id／by_weight，判定框要即時變」。
 *
 * 前端算出來的只是**顯示**。過帳時後端用同一個函式重算一次並以後端結果為準；
 * 兩邊不一致就回 `409 JUDGEMENT_MISMATCH`（那通常代表設定在你填表期間被改了）。
 *
 * 為了跑 `judgePick`，這裡用 API 給的片中介資料組一個**最小的 World**
 * ——後端的 `ops/judge.ts` 用的是同樣的手法。
 */

import { useMemo, useState } from 'react'
import type { Config } from '../../../shared/types/config.js'
import type { PanelId } from '../../../shared/types/domain.js'
import type { DerivedItem, World } from '../../../shared/derive/world.js'
import { judgePick, type PickJudgement } from '../../../shared/derive/resolution.js'
import { count, kgUnit } from '../../../shared/lib/format.js'
import { HomogeneityBadge, ResolutionBadge } from './badges.js'
import { NumericKeypadInput } from './NumericKeypadInput.js'
import { Button, Mono, TextInput } from './ui.js'
import type { PanelMeta } from './WeighEntry.js'

export interface PickState {
  picked: PanelId[]
  splitBasis: 'by_id' | 'by_weight' | null
  byWeightKg: string
}

export function emptyPick(): PickState {
  return { picked: [], splitBasis: 'by_id', byWeightKg: '' }
}

/** API 回來的料件列 → judgePick 要的 DerivedItem。只填它用得到的欄位。 */
export function toDerivedItem(item: Record<string, unknown>, panelIds: string[]): DerivedItem {
  return {
    itemId: item.item_id as string,
    materialCode: item.material_code as DerivedItem['materialCode'],
    containerId: (item.container_id as string) ?? null,
    locationId: (item.location_id as string) ?? null,
    netKg: item.net_kg === null || item.net_kg === undefined ? null : Number(item.net_kg),
    panelCount: (item.panel_count as number) ?? null,
    resolutionGrade: item.resolution_grade as DerivedItem['resolutionGrade'],
    homogeneityGrade: item.homogeneity_grade as DerivedItem['homogeneityGrade'],
    status: item.status as DerivedItem['status'],
    sourceReceiptLineId: (item.source_receipt_line_id as string) ?? null,
    createdAt: String(item.created_at ?? ''),
    closedAt: (item.closed_at as string) ?? null,
    panelIds,
    workOrderId: (item.work_order_id as string) ?? null,
    r3Taint: item.r3_taint === true,
    issuedPanelIds: [],
  }
}

/**
 * 最小 World。`judgePick` 只用到 `world.panels.get(p)?.modelCode`，
 * 所以其餘欄位不必存在——刻意不去湊一個完整的 World 出來。
 */
export function miniWorld(panels: PanelMeta[]): World {
  const map = new Map(panels.map((p) => [p.panel_id, { modelCode: p.model_code }]))
  return { panels: map } as unknown as World
}

export function usePickJudgement(
  item: DerivedItem, panels: PanelMeta[], config: Config | null, pick: PickState, wantCount: number,
): PickJudgement | null {
  const isPartial = wantCount > 0 && wantCount < item.panelIds.length
  return useMemo(() => {
    if (!config) return null
    return judgePick(miniWorld(panels), config, item, pick.splitBasis, isPartial ? pick.picked : [])
  }, [panels, config, item, pick.splitBasis, pick.picked, isPartial])
}

export function PanelPicker({
  item, panels, config, wantCount, value, onChange, judgement,
}: {
  item: DerivedItem
  panels: PanelMeta[]
  config: Config
  wantCount: number
  value: PickState
  onChange: (v: PickState) => void
  judgement: PickJudgement | null
}) {
  const [rangeFrom, setRangeFrom] = useState('')
  const [rangeTo, setRangeTo] = useState('')
  const [showAll, setShowAll] = useState(false)

  const isPartial = wantCount > 0 && wantCount < item.panelIds.length
  const meta = useMemo(() => new Map(panels.map((p) => [p.panel_id, p])), [panels])

  const models = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of item.panelIds) {
      const k = meta.get(p)?.model_code ?? '未辨識'
      m.set(k, (m.get(k) ?? 0) + 1)
    }
    return [...m.entries()]
  }, [item.panelIds, meta])

  const toggle = (p: PanelId) => {
    const set = new Set(value.picked)
    if (set.has(p)) set.delete(p)
    else set.add(p)
    onChange({ ...value, picked: item.panelIds.filter((x) => set.has(x)) })
  }

  const selectFirst = () => onChange({ ...value, picked: item.panelIds.slice(0, wantCount) })
  const selectRange = () => {
    const from = rangeFrom.trim()
    const to = rangeTo.trim()
    if (!from || !to) return
    onChange({ ...value, picked: item.panelIds.filter((p) => p >= from && p <= to) })
  }

  const visible = showAll ? item.panelIds : item.panelIds.slice(0, 60)

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
        <p>
          料件 <Mono>{item.itemId}</Mono>（{count(item.panelIds.length)} 片 ·
          型號 {models.map(([m, c]) => `${m}×${c}`).join('、')}）
        </p>
        <p className="mt-0.5 text-xs text-ink-500">
          帳面淨重 {kgUnit(item.netKg, 1)}
          {item.netKg === null && ' — 進庫未過磅，本料件無重量帳'}
          {item.r3Taint && ' · 本料件曾以重量切分，已帶 R3 汙染'}
        </p>
      </div>

      {!isPartial ? (
        <p className="text-sm text-ink-600">整件領出 {count(item.panelIds.length)} 片，不需挑選。</p>
      ) : (
        <>
          <p className="text-sm">
            要領 <strong className="tabular-nums">{count(wantCount)}</strong> 片，請挑選：
          </p>

          <div className="flex flex-wrap items-end gap-2">
            <Button size="sm" onClick={selectFirst}>全選前 {wantCount} 片</Button>
            <div className="flex items-end gap-1">
              <TextInput value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)}
                placeholder="起 PV 號" className="w-[190px] font-mono text-xs" />
              <span className="pb-2 text-ink-400">–</span>
              <TextInput value={rangeTo} onChange={(e) => setRangeTo(e.target.value)}
                placeholder="迄 PV 號" className="w-[190px] font-mono text-xs" />
              <Button size="sm" onClick={selectRange}>依 PV 號範圍</Button>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onChange({ ...value, picked: [] })}>清除</Button>
            <span className={`ml-auto text-sm tabular-nums ${
              value.picked.length === wantCount ? 'font-medium text-emerald-700' : 'text-amber-700'}`}
            >已挑 {count(value.picked.length)} / {count(wantCount)}</span>
          </div>

          <div className="max-h-56 overflow-y-auto rounded-md border border-ink-200 p-2">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-x-3 gap-y-1">
              {visible.map((p) => {
                const m = meta.get(p)
                const on = value.picked.includes(p)
                return (
                  <label key={p}
                    className={`flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs ${
                      on ? 'bg-sky-50' : 'hover:bg-ink-50'}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(p)} className="h-4 w-4" />
                    <span className="truncate font-mono">{p}</span>
                    {m?.condition_grade === 'damaged' && <span title="破損" className="text-red-600">破</span>}
                    {!m?.regulatory_id && <span title="無列管編碼" className="text-amber-600">無管</span>}
                  </label>
                )
              })}
            </div>
            {!showAll && item.panelIds.length > 60 && (
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => setShowAll(true)}>
                顯示全部 {count(item.panelIds.length)} 片
              </Button>
            )}
          </div>

          <fieldset className="rounded-md border border-ink-200 p-3">
            <legend className="px-1 text-xs font-medium text-ink-600">分裂方式</legend>
            <div className="flex flex-wrap gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="radio" name="splitBasis" className="h-4 w-4"
                  checked={value.splitBasis === 'by_id'}
                  onChange={() => onChange({ ...value, splitBasis: 'by_id' })} />
                按 PV 號挑選
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="radio" name="splitBasis" className="h-4 w-4"
                  checked={value.splitBasis === 'by_weight'}
                  onChange={() => onChange({ ...value, splitBasis: 'by_weight' })} />
                按重量切分
              </label>
            </div>
            {value.splitBasis === 'by_weight' && (
              <div className="mt-3 max-w-[220px]">
                <NumericKeypadInput value={value.byWeightKg} decimals={1}
                  onChange={(v) => onChange({ ...value, byWeightKg: v })} />
                <p className="mt-1 text-[11px] text-ink-500">要切出的重量。片數不再是切分依據。</p>
              </div>
            )}
          </fieldset>
        </>
      )}

      {/* §7.5 即時判定：切換 by_id／by_weight 這一框就要變 */}
      {judgement && (
        <div className={`rounded-md border px-3 py-2.5 ${
          judgement.degraded ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-ink-500">解析度</span>
            <ResolutionBadge grade={judgement.resolutionGrade} reason={judgement.resolutionReason} />
            <span className="ml-2 text-xs text-ink-500">同質性</span>
            <HomogeneityBadge grade={judgement.homogeneityGrade} reason={judgement.homogeneityReason} />
            <span className="ml-2 text-xs text-ink-500">需核可</span>
            <span className={`text-sm font-medium ${
              judgement.needsApproval ? 'text-amber-800' : 'text-emerald-800'}`}>
              {judgement.needsApproval
                ? `是（${judgement.approvalRule === 'case_by_case' ? '個案' : '預核'}）`
                : '否'}
            </span>
          </div>
          <p className="mt-1.5 text-xs text-ink-700">{judgement.splitNote}</p>
          <p className="text-[11px] text-ink-500">{judgement.resolutionReason}</p>
        </div>
      )}
    </div>
  )
}
